import React from 'react';
import { CloudLightning, Zap, Shield, TriangleAlert as AlertTriangle } from 'lucide-react';
import type { Site, CoreInfo } from '../types';

interface StatsRowProps {
  site: Site;
  coreInfo: CoreInfo | null;
  isSyncing: boolean;
}

export const StatsRow: React.FC<StatsRowProps> = ({ site, coreInfo, isSyncing }) => {
  const stats = [
    {
      label: 'WP Version',
      value: isSyncing ? '...' : (coreInfo?.wordpress?.version ?? site.wpVersion),
      icon: CloudLightning,
      color: 'text-blue-500',
    },
    {
      label: 'PHP Version',
      value: isSyncing ? '...' : (coreInfo?.php?.version ?? '\u2014'),
      icon: Zap,
      color: 'text-purple-500',
    },
    {
      label: 'SSL Status',
      value: isSyncing ? '...' : (() => {
        const isEnabled = coreInfo?.ssl && typeof coreInfo.ssl === 'object' ? coreInfo.ssl.enabled : coreInfo?.ssl;
        if (!isEnabled) return 'Missing';
        const daysLeft = coreInfo?.ssl && typeof coreInfo.ssl === 'object' ? coreInfo.ssl.days_left : null;
        if (daysLeft !== null && typeof daysLeft !== 'undefined') {
          return `Secured \u2022 Expires in ${daysLeft} Days`;
        }
        return 'Secured';
      })(),
      icon: Shield,
      color: (coreInfo?.ssl && typeof coreInfo.ssl === 'object' ? coreInfo.ssl.enabled : coreInfo?.ssl) ? 'text-emerald-500' : 'text-red-500',
    },
    {
      label: 'Issues/Errors',
      value: isSyncing ? '...' : 'None detected',
      icon: AlertTriangle,
      color: 'text-emerald-500',
    },
  ];

  return (
    <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
      {stats.map((stat) => {
        const Icon = stat.icon;
        return (
          <div key={stat.label} className="bg-card border border-border rounded-xl p-4 flex items-center gap-3">
            <div className="p-2 rounded-lg bg-muted/50">
              <Icon className={`w-5 h-5 ${stat.color}`} />
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-xs text-muted-foreground">{stat.label}</p>
              <p className="text-sm font-bold text-foreground truncate">
                {stat.value as React.ReactNode}
              </p>
            </div>
          </div>
        );
      })}
    </div>
  );
};
